/**
 * Everything on the maintenance page that differs between the ten apps.
 *
 * The Figma frames share a layout, but NOT as much as first assumed. Verified
 * deviations, all from node geometry across the 20 frames:
 *
 *  - Logo aspect ratios run from 3.33:1 (Jubilee) to 6.32:1 (AI Dropship)
 *    against AliDrop's 4.26:1, so the logo box cannot be one fixed size.
 *  - Spocket and DropshipTool place the background rings 109px further right
 *    than the other eight.
 *  - The divider and card border are the app's own headline ink at low alpha,
 *    not a shared neutral — that only looked neutral on AliDrop because its ink
 *    is #191919, i.e. rgb(25,25,25).
 *  - Spocket's pill border is 14%, not the 27% the other checked frames use.
 *
 * Copying this component to a sibling repo means editing this file and nothing
 * else. Values live in plans/maintenance-pages-all-apps/src/tokens.json.
 *
 * This copy is for JUBILEE (Figma node 150:2436, config profile A —
 * structurally identical to AliDrop's frame, only the colour/logo/mobile-ring
 * block below differs).
 */

export type Promo = {
  name: string;
  headline: string;
  blurb: string;
  linkLabel: string;
  linkUrl: string;
};

export const DROPSHIPTOOL: Promo = {
  name: "Dropshiptool",
  headline: "Discover Winning Products and High-Performing Viral Ads",
  blurb:
    "Track over 3 million Shopify stores to uncover winning products, viral TikTok ads, and top-performing Facebook and Instagram creatives.",
  linkLabel: "dropshiptool.io",
  linkUrl:
    "https://app.dropshiptool.io/register?utm_source=jubilee&utm_medium=maintenance&utm_campaign=findwinningads",
};

/** Identical on every frame — kept here so the ten copies can't drift. */
export const SHARED = {
  status: "Maintenance in progress",
  statusShort: "Maintenance",
  body: "We're running scheduled work on the platform. Nothing is lost, and your store keeps selling. We'll be back shortly.",
  cta: "Back to Home",
  reassurance: "This page refreshes itself when we're live.",
  promoEyebrow: "While you wait",
};

export const APP = {
  name: "Jubilee",
  /** `${name} will be back soon.` — the only per-app string in the headline. */
  home: "/",
  promo: DROPSHIPTOOL,

  /** Headline ink. Also the source for the divider and card border. */
  ink: "#362E2C",
  /** Body, reassurance, eyebrow, blurb, pill label. */
  muted: "#818181",

  /** Logo box, measured from this app's own frame. Rendered with
   *  object-fit: contain, so a wrong value letterboxes rather than distorts.
   *  Jubilee's is the WIDEST-aspect box of the ten (3.33:1), so contain
   *  genuinely matters here. */
  logo: {
    src: "/maintenance-logo.svg",
    width: 124,
    height: 37.26,
    mobileWidth: 100,
    mobileHeight: 30.05,
  },

  /** Ring left offsets as a percentage of frame width. Jubilee's frame puts
   *  them at 781px and 725px of 1440, same as AliDrop's; the mobile offsets
   *  differ per app. */
  rings: { outer: 54.2, inner: 50.3, outerMobile: 58.8, innerMobile: 44.5 },

  /** Background glow. Radii are the SVG matrix scale x10 (gradientUnits is
   *  userSpaceOnUse with r=10). Spocket's differ: 1728x808 at 82%. */
  glow: { radiusX: 1570, radiusY: 734, atX: 93, mobileRadiusX: 426 },

  /** Status pill border alpha. Two-digit hex suffix. 45 = 27%, 24 = 14%. */
  pillBorderAlpha: "45",

  /**
   * Typography is per-app, not shared. Nine of the ten use Inter; Spocket uses
   * Avenir, confirmed both in its Figma frame and in its own src/index.css.
   * Jubilee is on the Inter/profile-A set, same weights as AliDrop.
   */
  font: {
    family:
      '"Inter Variable", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    headline: 600,
    body: 400,
    cta: 700,
    reassurance: 300,
    eyebrow: 500,
    promoName: 600,
    promoHeadline: 600,
    promoBlurb: 400,
    promoLink: 600,
  },
};
